import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBranches } from '../services/branchService';
import { getProducts } from '../services/productService';
import { getBranchInventory, createInventory } from '../services/transferService';
import { getBranchEquipment } from '../services/equipmentService';
import { formatCurrency } from '../utils/format';
import AjusteProductoModal from '../components/AjusteProductoModal';
import EquipoBranchModal from '../components/EquipoBranchModal';

const EMPTY_FORM = {
  productId: '',
  depositId: '',
  stock: '',
  stockMin: '',
  state: true,
};

function groupByProduct(inventory) {
  const map = new Map();
  inventory.forEach(item => {
    const pid = item.product?.id;
    if (!pid) return;
    if (!map.has(pid)) {
      map.set(pid, {
        productId: pid,
        product: item.product,
        totalStock: 0,
        stockMin: 0,
        deposits: [],
      });
    }
    const group = map.get(pid);
    group.totalStock += item.stock || 0;
    if (item.deposit?.main) {
      group.stockMin = item.stockMin || 0;
    }
    group.deposits.push({
      inventoryId: item.id,
      depositId: item.deposit?.id,
      depositName: item.deposit?.name,
      main: item.deposit?.main,
      stock: item.stock,
      stockMin: item.stockMin,
      stockOrder: item.stockOrder,
      state: item.state,
    });
  });
  return Array.from(map.values());
}

export default function InventarioSucursal() {
  const { id } = useParams();
  const navigate = useNavigate();
  const branchId = Number(id);

  const [branch, setBranch] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [activeTab, setActiveTab] = useState('productos');

  const [showModal, setShowModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [equipmentInventory, setEquipmentInventory] = useState([]);
  const [loadingEquipment, setLoadingEquipment] = useState(true);
  const [equipmentError, setEquipmentError] = useState('');
  const [showAddEquipo, setShowAddEquipo] = useState(false);
  const [editingEquipo, setEditingEquipo] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [branches, inv] = await Promise.all([
        getBranches(),
        getBranchInventory(branchId),
      ]);
      const found = (Array.isArray(branches) ? branches : []).find(b => b.id === branchId);
      setBranch(found || null);
      setInventory(Array.isArray(inv) ? inv : []);
    } catch {
      setError('No se pudo cargar el inventario de la sucursal.');
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadEquipmentData = useCallback(async () => {
    setLoadingEquipment(true);
    setEquipmentError('');
    try {
      const data = await getBranchEquipment(branchId);
      setEquipmentInventory(Array.isArray(data) ? data : []);
    } catch {
      setEquipmentError('No se pudo cargar el inventario de equipos.');
    } finally {
      setLoadingEquipment(false);
    }
  }, [branchId]);

  useEffect(() => {
    loadEquipmentData();
  }, [loadEquipmentData]);

  const grouped = useMemo(() => groupByProduct(inventory), [inventory]);

  const totalValor = useMemo(() => (
    grouped.reduce((sum, item) => sum + (Number(item.product.cost) || 0) * (item.totalStock || 0), 0)
  ), [grouped]);

  async function openModal() {
    setForm(EMPTY_FORM);
    setFormError('');
    setShowModal(true);
    try {
      const prods = await getProducts();
      setProducts(Array.isArray(prods) ? prods : []);
    } catch {
      setProducts([]);
    }
  }

  function handleChange(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    try {
      const isMain = deposits.find(d => d.id === Number(form.depositId))?.main;
      await createInventory({
        productId: Number(form.productId),
        branchId,
        depositId: Number(form.depositId),
        stock: Number(form.stock),
        stockMin: isMain ? Number(form.stockMin || 0) : 0,
        stockOrder: 0,
        state: form.state,
      });
      setShowModal(false);
      await loadData();
    } catch (err) {
      setFormError(err.response?.data?.msg || 'No se pudo registrar el producto.');
    } finally {
      setSaving(false);
    }
  }

  const deposits = branch?.deposits?.filter(d => d.state) || [];
  const selectedDeposit = deposits.find(d => d.id === Number(form.depositId));

  return (
    <div className="page-fade" style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 22, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            className="btn-orig btn-orig-outline"
            onClick={() => navigate('/sucursales')}
            style={{ padding: '6px 10px' }}
            title="Volver a sucursales"
          >
            <i className="bi bi-arrow-left" style={{ fontSize: 16 }} />
          </button>
          <div>
            <p style={{ fontSize: 12, color: 'var(--orig-text-muted)', marginBottom: 3 }}>
              Inventario de sucursal
            </p>
            <h5 style={{
              fontWeight: 800, color: 'var(--orig-text)',
              fontFamily: 'var(--orig-font-display)', letterSpacing: 0.5, fontSize: 20,
            }}>
              {branch ? branch.name : `Sucursal #${branchId}`}
            </h5>
          </div>
        </div>
        {activeTab === 'productos' ? (
          <button className="btn-orig btn-orig-green" onClick={openModal}>
            <i className="bi bi-plus-lg" />
            Agregar Producto
          </button>
        ) : (
          <button className="btn-orig btn-orig-green" onClick={() => setShowAddEquipo(true)}>
            <i className="bi bi-plus-lg" />
            Agregar Equipo
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--orig-border)' }}>
        <button
          className="btn-orig-ghost"
          onClick={() => setActiveTab('productos')}
          style={{
            padding: '10px 18px', fontSize: 13, fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer',
            color: activeTab === 'productos' ? 'var(--orig-green-bright)' : 'var(--orig-text-muted)',
            borderBottom: activeTab === 'productos' ? '2px solid var(--orig-green)' : '2px solid transparent',
            marginBottom: -1,
          }}
        >
          <i className="bi bi-box-seam" style={{ marginRight: 6 }} />
          Productos
        </button>
        <button
          className="btn-orig-ghost"
          onClick={() => setActiveTab('equipos')}
          style={{
            padding: '10px 18px', fontSize: 13, fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer',
            color: activeTab === 'equipos' ? 'var(--orig-green-bright)' : 'var(--orig-text-muted)',
            borderBottom: activeTab === 'equipos' ? '2px solid var(--orig-green)' : '2px solid transparent',
            marginBottom: -1,
          }}
        >
          <i className="bi bi-pc-display" style={{ marginRight: 6 }} />
          Equipos
        </button>
      </div>

      {/* Tab: Productos */}
      {activeTab === 'productos' && (
      <div className="section-card">
        <div className="section-header">
          <h6>
            <i className="bi bi-clipboard-data" style={{ color: 'var(--orig-green)' }} />
            Productos en inventario
          </h6>
          {!loading && (
            <span className="badge-orig badge-green">
              {grouped.length} {grouped.length === 1 ? 'producto' : 'productos'}
            </span>
          )}
        </div>

        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--orig-text-muted)' }}>
              <i className="bi bi-arrow-repeat" style={{ fontSize: 28, display: 'block', marginBottom: 10, animation: 'spin 1s linear infinite' }} />
              Cargando inventario...
            </div>
          ) : error ? (
            <div style={{ padding: '28px 20px' }}>
              <div className="alert-orig alert-error">
                <i className="bi bi-exclamation-triangle-fill" />
                {error}
              </div>
            </div>
          ) : grouped.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--orig-text-muted)' }}>
              <i className="bi bi-inbox" style={{ fontSize: 30, display: 'block', marginBottom: 10, opacity: 0.4 }} />
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Sin productos</div>
              <div style={{ fontSize: 12 }}>Esta sucursal aún no tiene productos en inventario.</div>
            </div>
          ) : (
            <table className="orig-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Producto</th>
                  <th>Stock mínimo</th>
                  <th>Stock total</th>
                  <th>Costo</th>
                  <th>Valor total</th>
                  <th style={{ textAlign: 'center' }}>Depósitos</th>
                </tr>
              </thead>
              <tbody>
                {grouped.map((item, idx) => (
                  <tr key={item.productId}>
                    <td style={{ color: 'var(--orig-text-muted)', fontSize: 12, fontFamily: 'monospace' }}>
                      {idx + 1}
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{item.product.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--orig-text-muted)' }}>
                        {item.product.packageType}
                        {item.product.packageQuantity ? ` × ${item.product.packageQuantity}` : ''}
                      </div>
                    </td>
                    <td style={{ fontSize: 12.5, color: 'var(--orig-text-muted)' }}>{item.stockMin}</td>
                    <td>
                      <span style={{
                        fontSize: 14, fontWeight: 700,
                        color: item.stockMin > 0 && item.totalStock <= item.stockMin
                          ? 'var(--orig-red-light)' : 'var(--orig-text)',
                      }}>
                        {item.totalStock}
                      </span>
                    </td>
                    <td style={{ fontSize: 12.5, color: 'var(--orig-text-muted)' }}>{formatCurrency(item.product.cost)}</td>
                    <td style={{ fontSize: 13, fontWeight: 600, color: 'var(--orig-text)' }}>
                      {formatCurrency(item.product.cost * item.totalStock)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        <button
                          className="btn-orig btn-orig-ghost"
                          onClick={() => setSelectedProduct(item)}
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          title="Ver depósitos"
                        >
                          <i className="bi bi-box-seam" />
                          {item.deposits.length}
                        </button>
                        <button
                          className="btn-orig btn-orig-outline"
                          onClick={() => setEditingItem({ ...item, branchName: branch ? branch.name : `Sucursal #${branchId}` })}
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          title="Ajustar costo, precio e inventario"
                        >
                          <i className="bi bi-sliders" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--orig-border)' }}>
                  <td colSpan={5} style={{ textAlign: 'right', fontSize: 12.5, fontWeight: 600, color: 'var(--orig-text-muted)' }}>
                    Total general
                  </td>
                  <td style={{ fontSize: 14, fontWeight: 800, color: 'var(--orig-green-bright)' }}>
                    {formatCurrency(totalValor)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
      )}

      {/* Tab: Equipos */}
      {activeTab === 'equipos' && (
      <div className="section-card">
        <div className="section-header">
          <h6>
            <i className="bi bi-pc-display" style={{ color: 'var(--orig-green)' }} />
            Equipos en inventario
          </h6>
          {!loadingEquipment && (
            <span className="badge-orig badge-green">
              {equipmentInventory.length} {equipmentInventory.length === 1 ? 'equipo' : 'equipos'}
            </span>
          )}
        </div>

        <div style={{ overflowX: 'auto' }}>
          {loadingEquipment ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--orig-text-muted)' }}>
              <i className="bi bi-arrow-repeat" style={{ fontSize: 28, display: 'block', marginBottom: 10, animation: 'spin 1s linear infinite' }} />
              Cargando equipos...
            </div>
          ) : equipmentError ? (
            <div style={{ padding: '28px 20px' }}>
              <div className="alert-orig alert-error">
                <i className="bi bi-exclamation-triangle-fill" />
                {equipmentError}
              </div>
            </div>
          ) : equipmentInventory.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--orig-text-muted)' }}>
              <i className="bi bi-inbox" style={{ fontSize: 30, display: 'block', marginBottom: 10, opacity: 0.4 }} />
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Sin equipos</div>
              <div style={{ fontSize: 12 }}>Esta sucursal aún no tiene equipos en inventario.</div>
            </div>
          ) : (
            <table className="orig-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Equipo</th>
                  <th>Tipo</th>
                  <th>Cantidad</th>
                  <th style={{ textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {equipmentInventory.map((item, idx) => (
                  <tr key={item.id}>
                    <td style={{ color: 'var(--orig-text-muted)', fontSize: 12, fontFamily: 'monospace' }}>
                      {idx + 1}
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{item.equipment?.name}</div>
                      {item.equipment?.brand && (
                        <div style={{ fontSize: 11, color: 'var(--orig-text-muted)' }}>{item.equipment.brand}</div>
                      )}
                    </td>
                    <td style={{ fontSize: 12.5 }}>
                      {item.equipment?.type
                        ? <span style={{ background: 'var(--orig-teal-dim)', color: 'var(--orig-teal-light)', padding: '2px 9px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{item.equipment.type}</span>
                        : <span style={{ color: 'var(--orig-text-hint)' }}>—</span>}
                    </td>
                    <td style={{ fontSize: 14, fontWeight: 700, color: 'var(--orig-text)' }}>{item.quantity}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        className="btn-orig btn-orig-outline"
                        onClick={() => setEditingEquipo(item)}
                        style={{ padding: '4px 10px', fontSize: 12 }}
                        title="Editar equipo"
                      >
                        <i className="bi bi-pencil" />
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      )}

      {/* Modal depósitos */}
      {selectedProduct && (
        <div
          onClick={() => setSelectedProduct(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="section-card"
            style={{ width: '100%', maxWidth: 520, marginBottom: 0, maxHeight: '90vh', overflowY: 'auto' }}
          >
            <div className="section-header">
              <h6>
                <i className="bi bi-box-seam" style={{ color: 'var(--orig-green)' }} />
                Detalle por depósito
              </h6>
              <button
                type="button"
                onClick={() => setSelectedProduct(null)}
                style={{ background: 'none', border: 'none', color: 'var(--orig-text-muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
                title="Cerrar"
              >
                ×
              </button>
            </div>

            <div className="section-body" style={{ padding: '20px' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 16px', marginBottom: 20,
                background: 'var(--orig-panel)', borderRadius: 'var(--orig-radius)',
                border: '1px solid var(--orig-border)',
              }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 'var(--orig-radius)',
                  background: 'var(--orig-green-dim)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <i className="bi bi-box-fill" style={{ fontSize: 18, color: 'var(--orig-green-bright)' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--orig-text)' }}>
                    {selectedProduct.product.name}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--orig-text-muted)' }}>
                    {selectedProduct.product.packageType}
                    {selectedProduct.product.packageQuantity ? ` × ${selectedProduct.product.packageQuantity}` : ''}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                <div style={{
                  flex: 1, padding: '12px 14px',
                  background: 'var(--orig-green-dim)', borderRadius: 'var(--orig-radius)',
                  border: '1px solid rgba(5,150,105,0.2)',
                }}>
                  <div style={{ fontSize: 11, color: 'var(--orig-text-muted)', marginBottom: 2 }}>Stock total</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--orig-green-bright)' }}>
                    {selectedProduct.totalStock}
                  </div>
                </div>
                <div style={{
                  flex: 1, padding: '12px 14px',
                  background: 'var(--orig-panel)', borderRadius: 'var(--orig-radius)',
                  border: '1px solid var(--orig-border)',
                }}>
                  <div style={{ fontSize: 11, color: 'var(--orig-text-muted)', marginBottom: 2 }}>Stock mínimo</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--orig-text)' }}>
                    {selectedProduct.stockMin}
                  </div>
                </div>
              </div>

              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--orig-text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Distribución por depósito
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {selectedProduct.deposits.map(dep => {
                  return (
                    <div key={dep.depositId} style={{
                      padding: '12px 14px',
                      background: 'var(--orig-panel)', borderRadius: 'var(--orig-radius)',
                      border: '1px solid var(--orig-border)',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--orig-text)' }}>
                            {dep.depositName}
                          </span>
                          {dep.main && (
                            <span style={{
                              background: 'var(--orig-green-dim)',
                              color: 'var(--orig-green-bright)',
                              padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                              display: 'inline-flex', alignItems: 'center', gap: 3,
                            }}>
                              <i className="bi bi-star-fill" style={{ fontSize: 8 }} />
                              Principal
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--orig-text)' }}>
                          {dep.stock}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal ajuste de costo/precio/inventario */}
      {editingItem && (
        <AjusteProductoModal
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSaved={() => { setEditingItem(null); loadData(); }}
        />
      )}

      {/* Modal agregar equipo */}
      {showAddEquipo && (
        <EquipoBranchModal
          branchId={branchId}
          onClose={() => setShowAddEquipo(false)}
          onSaved={() => { setShowAddEquipo(false); loadEquipmentData(); }}
        />
      )}

      {/* Modal editar equipo */}
      {editingEquipo && (
        <EquipoBranchModal
          branchId={branchId}
          existing={editingEquipo}
          onClose={() => setEditingEquipo(null)}
          onSaved={() => { setEditingEquipo(null); loadEquipmentData(); }}
        />
      )}

      {/* Modal agregar producto */}
      {showModal && (
        <div
          onClick={() => setShowModal(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="section-card"
            style={{ width: '100%', maxWidth: 520, marginBottom: 0, maxHeight: '90vh', overflowY: 'auto' }}
          >
            <div className="section-header">
              <h6>
                <i className="bi bi-plus-circle" style={{ color: 'var(--orig-green)' }} />
                Agregar Producto al Inventario
              </h6>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', color: 'var(--orig-text-muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
                title="Cerrar"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="section-body">
                {formError && (
                  <div className="alert-orig alert-error" style={{ marginBottom: 16 }}>
                    <i className="bi bi-exclamation-triangle-fill" />
                    {formError}
                  </div>
                )}

                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label">Producto</label>
                    <select
                      className="form-control"
                      value={form.productId}
                      onChange={e => handleChange('productId', e.target.value)}
                      required
                    >
                      <option value="">Seleccione un producto</option>
                      {products.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name} — {p.packageType}{p.packageQuantity ? ` × ${p.packageQuantity}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12">
                    <label className="form-label">Depósito</label>
                    <select
                      className="form-control"
                      value={form.depositId}
                      onChange={e => handleChange('depositId', e.target.value)}
                      required
                    >
                      <option value="">Seleccione un depósito</option>
                      {deposits.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.name}{d.main ? ' (Principal)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={selectedDeposit?.main ? 'col-6' : 'col-12'}>
                    <label className="form-label">Stock</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="0"
                      min="0"
                      value={form.stock}
                      onChange={e => handleChange('stock', e.target.value)}
                      required
                    />
                  </div>

                  {selectedDeposit?.main && (
                    <div className="col-6">
                      <label className="form-label">Stock mínimo</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0"
                        min="0"
                        value={form.stockMin}
                        onChange={e => handleChange('stockMin', e.target.value)}
                      />
                    </div>
                  )}

                  <div className="col-12">
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={form.state}
                        onChange={e => handleChange('state', e.target.checked)}
                        style={{ accentColor: 'var(--orig-green)', width: 16, height: 16 }}
                      />
                      Activo
                    </label>
                  </div>
                </div>
              </div>

              <div style={{
                display: 'flex', justifyContent: 'flex-end', gap: 10,
                padding: '14px 20px', borderTop: '1px solid var(--orig-border)',
              }}>
                <button type="button" className="btn-orig btn-orig-outline" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-orig btn-orig-green" disabled={saving}>
                  <i className="bi bi-check-lg" />
                  {saving ? 'Guardando...' : 'Agregar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
